import { useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useVisionCheck } from "../../hooks/useVisionCheck";
import { Loader2 } from "lucide-react";
import { BottomNav } from "./BottomNav";

interface VisionValidationProps {
  children: React.ReactNode;
}

export const BottomNavValidation: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { hasVision, isChecking, clearVisionCache } = useVisionCheck({ skipRedirect: true });
  const location = useLocation();

  // Clear cache if hasVision is false but we're on profile page (force refresh)
  if (hasVision === false && location.pathname === '/profile') {  
    clearVisionCache?.();
  }

  // Don't show bottom nav if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  // Always show bottom nav on profile page regardless of vision status
  if (location.pathname === '/profile') {
    return <BottomNav />;
  }

  // Don't show bottom nav if still checking or no vision
  if (isChecking || hasVision === false) {
    return null;
  }

  // Only show bottom nav if user has vision
  return <BottomNav />;
}

export const VisionValidation: React.FC<VisionValidationProps> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const exemptRoutes = useMemo(
    () => [
      "/login",
      "/register",
      "/recover-password",
      "/validate-code",
      "/reset-password",
      "/onboarding",
      "/chat-onboarding",
      "/onboarding-summary",
    ],
    []
  );

  const { isChecking } = useVisionCheck({
    onNoVision: () => {
      if (!exemptRoutes.includes(location.pathname)) {
        navigate("/onboarding");
      }
    }
  });

  // Skip validation for exempt routes
  if (exemptRoutes.includes(location.pathname) || !isAuthenticated) {
    return <>{children}</>;
  }

  if (isChecking) {
    return (
      <div className="h-screen bg-[#1a1a1a] flex flex-col items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-[#5CD2F4] animate-spin mx-auto" />
          <p className="text-white text-lg">Checking your vision...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
